import "bootstrap/dist/css/bootstrap.min.css";
import { get, onValue, push, ref } from "firebase/database";
import { useEffect, useState } from "react";
import MaskedDisplay from "../components/MaskedDisplay";
import MaskedInput from "../components/MaskedInput";
import { db } from "../Firebase";
import { validateAadhaar, validatePhone } from "../utils/fieldValidation";

/* ================= Reusable Field ================= */
const Field = ({ label, children }) => (
  <div className="col-md-4 mb-3">
    <label className="form-label fw-semibold">{label}</label>
    {children}
  </div>
);

const InformationPage = () => {
  const [regId, setRegId] = useState("");
  const [basic, setBasic] = useState(null);
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadedId, setLoadedId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");

  // Master data
  const [states, setStates] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);

  /* ================= RELATIONSHIP OPTIONS ================= */
  const relationshipOptions = [
    "मुखिया स्वयं",
    "पत्नी",
    "पुत्र",
    "पुत्री",
    "बहू",
    "सगी बहन",
    "पारिवारिक बहन",
    "सगा भाई",
    "पारिवारिक भाई",
    "दादा",
    "दादी",
    "चाचा",
    "चाची",
    "बड़े पापा",
    "बड़ी मम्मी",
    "बुआ",
    "फूपा",
    "नाना",
    "नानी",
    "मामा",
    "मामी",
    "मोसी",
    "मोसिया",
    "व्यावहारिक रिश्ता",
    "अन्य"
  ];

  /* ================= FORM ================= */
  const [form, setForm] = useState({
    name: "",
    fatherName: "",
    gender: "",
    relation: "",
    dob: "",
    aadhaar: "",
    maritalStatus: "",
    mobile: "",
    whatsapp: "",
    email: "",
    bloodGroup: "",
    currentAddress: "",
    birthAddress: "",
    educationStatus: "",
    educationState: "",
    educationDistrict: "",
    educationClass: "",
    educationSubject: "",
    workType: "",
    workState: "",
    workDistrict: "",
    workArea: "",
    workAreaName: "",
    workDepartment: "",
    workPosition: "",
  });

  /* ================= LOAD MASTER DATA ================= */
  useEffect(() => {
    const load = (path, setter) =>
      onValue(ref(db, path), snap =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : []
        )
      );

    load("masters/states", setStates);
    load("masters/workTypes", setWorkTypes);
    load("masters/workAreas", setWorkAreas);
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "aadhaar") {
      setForm(p => ({ ...p, aadhaar: value.replace(/\s/g, "") }));
      return;
    }

    setForm(p => ({ ...p, [name]: value }));
  };

  const generate7DigitId = () =>
    Math.floor(1000000 + Math.random() * 9000000).toString();

  const computeAge = (dob) => {
    if (!dob) return "-";
    const d = new Date(dob);
    if (isNaN(d.getTime())) return "-";
    return Math.floor(
      (Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    );
  };

  /* ================= SEARCH ================= */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!regId || !passwordInput) {
      alert("ID और पासवर्ड दर्ज करें");
      return;
    }

    const snap = await get(ref(db, `registration/${regId}`));
    if (!snap.exists()) {
      alert("रिकॉर्ड नहीं मिला");
      return;
    }

    const data = snap.val();
    if (data.meta?.password !== passwordInput) {
      alert("गलत पासवर्ड");
      return;
    }

    setBasic(data.basic || {});
    setMeta(data.meta || {});
    setMembers(data.members ? Object.values(data.members) : []);
    setLoadedId(regId);
  };

  /* ================= ADD MEMBER ================= */
  const handleAddMember = async (e) => {
    e.preventDefault();

    if (!form.name || !form.relation) {
      alert("नाम और संबंध अनिवार्य हैं");
      return;
    }

    if (form.aadhaar && !validateAadhaar(form.aadhaar)) {
      alert("अवैध Aadhaar");
      return;
    }

    if (form.mobile && !validatePhone(form.mobile)) {
      alert("अवैध मोबाइल");
      return;
    }

    const memberId = generate7DigitId();
    await push(
      ref(db, `registration/${loadedId}/members`),
      { ...form, memberId }
    );

    alert("सदस्य जोड़ा गया");
    setShowModal(false);
    setForm({
      name: "",
      fatherName: "",
      gender: "",
      relation: "",
      dob: "",
      aadhaar: "",
      maritalStatus: "",
      mobile: "",
      whatsapp: "",
      email: "",
      bloodGroup: "",
      currentAddress: "",
      birthAddress: "",
      educationStatus: "",
      educationState: "",
      educationDistrict: "",
      educationClass: "",
      educationSubject: "",
      workType: "",
      workState: "",
      workDistrict: "",
      workArea: "",
      workAreaName: "",
      workDepartment: "",
      workPosition: "",
    });
  };

  /* ================= UI ================= */
  return (
    <div className="container py-4">
      <h3 className="text-center text-success mb-4">
        👨‍👩‍👧‍👦 परिवार सदस्य प्रबंधन
      </h3>

      {!basic && (
        <form onSubmit={handleSearch} className="row g-3 mb-4">
          <Field label="परिवार ID">
            <input className="form-control" value={regId}
              onChange={e => setRegId(e.target.value)} />
          </Field>
          <Field label="पासवर्ड">
            <input type="password" className="form-control"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)} />
          </Field>
          <div className="col-md-4 d-flex align-items-end">
            <button className="btn btn-primary w-100">🔍 खोजें</button>
          </div>
        </form>
      )}

      {basic && (
        <>
          <table className="table table-bordered">
            <tbody>
              <tr>
                <th>मुखिया</th>
                <td>{basic.headName}</td>
                <th>मोबाइल</th>
                <td><MaskedDisplay type="phone" value={basic.mobile} /></td>
              </tr>
            </tbody>
          </table>

          <h5 className="mt-4">👥 सदस्य</h5>
          <table className="table table-bordered text-center">
            <thead className="table-dark">
              <tr>
                <th>#</th><th>ID</th><th>नाम</th><th>उम्र</th><th>संबंध</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{m.memberId}</td>
                  <td>{m.name}</td>
                  <td>{computeAge(m.dob)}</td>
                  <td>{m.relation}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="btn btn-success"
            onClick={() => setShowModal(true)}>
            ➕ नया सदस्य
          </button>
        </>
      )}

      {showModal && (
        <div className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content p-3">
              <h5>नया सदस्य</h5>
              <form onSubmit={handleAddMember}>
                <div className="row g-3">
                  <Field label="नाम *">
                    <input className="form-control"
                      name="name" value={form.name}
                      onChange={handleChange} />
                  </Field>

                  <Field label="मुखिया से संबंध *">
                    <select className="form-select"
                      name="relation" value={form.relation}
                      onChange={handleChange}>
                      <option value="">संबंध चुनें</option>
                      {relationshipOptions.map((relation, index) => (
                        <option key={index} value={relation}>{relation}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="लिंग">
                    <select className="form-select"
                      name="gender" value={form.gender}
                      onChange={handleChange}>
                      <option value="">चुनें</option>
                      <option value="पुरुष">पुरुष</option>
                      <option value="महिला">महिला</option>
                      <option value="अन्य">अन्य</option>
                    </select>
                  </Field>

                  <Field label="जन्म तिथि">
                    <input type="date" className="form-control"
                      name="dob" value={form.dob}
                      onChange={handleChange} />
                  </Field>

                  <Field label="Aadhaar">
                    <MaskedInput
                      type="aadhaar"
                      name="aadhaar"
                      value={form.aadhaar}
                      onChange={handleChange}
                    />
                  </Field>

                  <Field label="मोबाइल">
                    <MaskedInput
                      type="phone"
                      name="mobile"
                      value={form.mobile}
                      onChange={handleChange}
                    />
                  </Field>

                  <Field label="WhatsApp">
                    <MaskedInput
                      type="phone"
                      name="whatsapp"
                      value={form.whatsapp}
                      onChange={handleChange}
                    />
                  </Field>

                  <Field label="ईमेल">
                    <input type="email" className="form-control"
                      name="email" value={form.email}
                      onChange={handleChange} />
                  </Field>

                  <Field label="वैवाहिक स्थिति">
                    <select className="form-select"
                      name="maritalStatus" value={form.maritalStatus}
                      onChange={handleChange}>
                      <option value="">चुनें</option>
                      <option value="अविवाहित">अविवाहित</option>
                      <option value="विवाहित">विवाहित</option>
                      <option value="विधवा/विधुर">विधवा/विधुर</option>
                      <option value="तलाकशुदा">तलाकशुदा</option>
                    </select>
                  </Field>
                </div>

                <div className="text-end mt-3">
                  <button type="button" className="btn btn-secondary me-2"
                    onClick={() => setShowModal(false)}>
                    रद्द करें
                  </button>
                  <button type="submit" className="btn btn-success">
                    सेव करें
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformationPage;
